import React, { useState } from 'react';
import { Box, Menu, MenuItem, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { downloadEngineHoursTimeline } from 'views/EngineHours/Components/utils';
import DriverBehaviorHandler from 'handlers/driver.behavior.handler';
import dayjs from 'dayjs';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useAppSelector } from 'store/hook';
import { isTpmsOnlyCustomer } from 'utils/helpers/general';
import { useClientTheme } from 'utils/clientTheme';
import { useTranslation } from 'react-i18next';

interface DownloadDropdownProps {
  sx?: object;
}

export const DownloadDropdown: React.FC<DownloadDropdownProps> = ({
  sx = {},
}) => {
  const { t } = useTranslation();
  const palette = useClientTheme();
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [disableDownload, setdisableDownload] = useState(false);

  const driverBehaviorHandler = new DriverBehaviorHandler();
  const { preferredTimeDisplayMode, timezoneBasedOnDisplayMode } =
    useTimeDisplayMode();
  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const modules = useAppSelector((state) => state.authReducer.customer.modules);

  const yesterdayStart = dayjs()
    .subtract(1, 'day')
    .startOf('day')
    .format('YYYY-MM-DD HH:mm:ss');
  const yesterdayEnd = dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss');

  // For today: from midnight today to current time
  const todayStart = dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss');
  const todayEnd = dayjs().format('YYYY-MM-DD HH:mm:ss');

  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (disableDownload) {
      return;
    }
    setDownloadAnchorEl(event.currentTarget);
    setIsDownloadMenuOpen(true);
  };

  const handleDownloadMenuClose = () => {
    setDownloadAnchorEl(null);
    setIsDownloadMenuOpen(false);
  };

  const handleDownloadReport = async (period: 'yesterday' | 'today') => {
    console.log(`Downloading report for ${period}`);

    try {
      if (period === 'yesterday') {
        await downloadEngineHoursTimeline(
          'pdf',
          yesterdayStart,
          yesterdayEnd,
          customerId ?? '',
          preferredTimeDisplayMode,
          timezoneBasedOnDisplayMode,
          driverBehaviorHandler,
          setIsExporting,
          setdisableDownload,
          true
        );
      } else {
        await downloadEngineHoursTimeline(
          'pdf',
          todayStart,
          todayEnd,
          customerId ?? '',
          preferredTimeDisplayMode,
          timezoneBasedOnDisplayMode,
          driverBehaviorHandler,
          setIsExporting,
          setdisableDownload,
          true
        );
      }
    } catch (error) {
      console.error('Error downloading report:', error);
    } finally {
      handleDownloadMenuClose();
    }
  };

  // disable site-summary for tpms-only-customers [as no data available]
  if (isTpmsOnlyCustomer(modules)) return null;

  return isExporting ? (
    <>
      <Typography color="white">Exporting...</Typography>
    </>
  ) : (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '10px',
          padding: '6px 5px',
          height: '10%',
          width: '120px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          color: 'rgba(255, 255, 255, 0.9)',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.1)',
          },
          ...sx,
        }}
        onMouseEnter={handleDownloadMenuOpen}
      >
        <Typography
          sx={{
            fontSize: '12px',
            lineHeight: '16px',
            marginTop: '-6px',
            color: 'inherit',
          }}
        >
          {t('site_summary_report')}
        </Typography>
        <DownloadIcon
          sx={{
            color: 'inherit',
            fontSize: '20px',
          }}
        />
      </Box>

      <Menu
        anchorEl={downloadAnchorEl}
        open={isDownloadMenuOpen}
        onClose={handleDownloadMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        MenuListProps={{
          onMouseLeave: handleDownloadMenuClose,
        }}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: `${palette.menuOptions} !important`,
            color: palette.textPrimary,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            minWidth: '180px',
            border: `1px solid ${palette.border}`,
          },
          '& .MuiMenuItem-root': {
            color: palette.textPrimary,
            fontSize: '12px',
            '&:hover': {
              backgroundColor: palette.sectionBackground,
              color: palette.textPrimary,
            },
          },
          '& .MuiList-root': {
            padding: '4px 0',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            void handleDownloadReport('yesterday');
          }}
          sx={{
            fontWeight: 'bold',
            fontSize: '12px',
            backgroundColor: palette.toggleSelected,
            color: palette.white,
          }}
        >
          📊 {t('yesterdays_report')}
        </MenuItem>
        <MenuItem
          sx={{
            fontSize: '12px',
            color: palette.textPrimary,
          }}
          onClick={() => {
            void handleDownloadReport('today');
          }}
        >
          📈 {t('todays_report')}
        </MenuItem>
      </Menu>
    </>
  );
};

export default DownloadDropdown;
